/* ═══════════════════════════════════════════════════════════════════════════
   rfhq_owned_theme.js — site-theme fallback for any [data-rfhq-owned] surface

   Extracted from product_card/grid.js's original _applySiteThemeFallback(),
   which only ever ran for RfhqProductGrid's own container. Every other
   websitehq-owned surface that isn't a Web Page (order confirmation, order
   detail, order history, wishlist, ...) needs the exact same fallback:
   .rfhq-page (web_page_decoration.py) is the one place a site theme default
   is safe to stamp, since it only ever wraps a page's own RFHQ block
   content — but it only ever wraps Web Page documents. Anything else that
   claims data-rfhq-owned has no themed ancestor to inherit from at all
   unless this runs.

   window.rfhqApplyOwnedThemeFallback(el) is the one implementation; grid.js
   calls it directly instead of keeping its own copy. This file additionally
   sweeps the whole page on load and on every DOM insert (via rfhq_dom.js's
   shared observer), so a plain server-rendered [data-rfhq-owned] page (no
   grid involved at all) gets the same treatment for free.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /**
   * Stamp the site's default theme onto `el`, but ONLY when nothing between
   * `el` and the document root already provides one — a themed RFHQ Section
   * (explicit, or itself inherited from .rfhq-page) must always keep
   * winning over this fallback, never be overwritten by it.
   *
   * <body> itself is excluded from that ancestor check (see
   * rfhq_body_theme.js, which claims it as a page-wide default so the
   * header/margins on non-Section pages like PDP/Cart aren't left on the
   * page's own hardcoded literal background). Body is a global default, not
   * a genuine closer override — treating it like one would silently break
   * every other owned root container (order pages, wishlist, trial room,
   * #product-filters, ...): several of them paint --rfhq-section-bg etc as
   * THEIR OWN background via rfhq_templates.css's Layer-3 "[data-rfhq-owned]
   * { --rfhq-section-bg: var(--rfhq-color-bg); ... }" component-defaults
   * block, and a directly-set custom property always wins over one merely
   * inherited from body — so without their own data-rfhq-theme stamp
   * winning that same-element tie-break (by file order, against Layer 3),
   * they'd fall back to the generic light default instead of the real site
   * theme. Found live: PDP/Cart went white again the moment body itself
   * became a themed ancestor.
   */
  function applyOwnedThemeFallback(el) {
    if (!el || typeof el.closest !== 'function') return;
    if (el.hasAttribute('data-rfhq-theme')) return;
    var ancestor = el.parentElement && el.parentElement.closest('[data-rfhq-theme]');
    if (ancestor && ancestor !== document.body) return;
    var site = typeof window !== 'undefined' && window.rfhqSiteTheme;
    if (!site) return;
    el.setAttribute('data-rfhq-theme', site);
    // Marks this stamp as "same value the ambient ancestor already has",
    // never a genuinely different, explicitly-chosen theme (an admin's own
    // Section-level pick is a server-rendered data-rfhq-theme attribute,
    // never touched by this function at all). rfhq_templates.css uses this
    // to skip repainting --rfhq-section-bg on an element that already sits
    // inside a [data-rfhq-theme] ancestor - harmless for a flat colour, but
    // --rfhq-section-bg can be a linear-gradient() under the Gradient-*
    // presets, and a gradient is positioned relative to whichever box
    // paints it: two nested elements each independently painting "the
    // same" gradient creates a visible seam where the inner one restarts
    // partway down the page. Found live on /all-products: the grid row and
    // filter sidebar both stamped here (so their children have tokens to
    // read even when nothing else provides a themed ancestor) but both
    // ALSO nested inside <body>, which already paints the full gradient.
    el.setAttribute('data-rfhq-theme-inherited', '1');
  }

  function sweep(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (root.matches && root.matches('[data-rfhq-owned]')) applyOwnedThemeFallback(root);
    root.querySelectorAll('[data-rfhq-owned]').forEach(applyOwnedThemeFallback);
  }

  window.rfhqApplyOwnedThemeFallback = applyOwnedThemeFallback;

  frappe.ready(function () {
    sweep(document.body);
    if (window.rfhqDom && typeof window.rfhqDom.onAdded === 'function') {
      window.rfhqDom.onAdded(function (roots) {
        roots.forEach(sweep);
      });
    }
  });
}());
